/**
 * LightSignatureManager — Feature manager for animated light signature meshes.
 *
 * Replaces 4 named GLB materials with controlled presets:
 *   - 2 glass lens presets (MeshPhysicalMaterial) for headlight / taillight lenses
 *   - 2 emissive light-mesh presets (MeshStandardMaterial + AdditiveBlending)
 *     for the white DRL strip and red tail-light strip
 *
 * Supports:
 *   - Animated on/off toggling via `emissiveIntensity` lerp (no shader recompile)
 *   - Optional position-light PNG texture applied to emissiveMap + alphaMap
 *   - If no URL is provided, the GLB's original `emissiveMap` is preserved
 *
 * Render-order strategy (prevents transparency sorting artefacts):
 *   - Opaque car body:   renderOrder = 0  (Three.js default)
 *   - Glass lenses:      renderOrder = 5  (renders after opaque body)
 *   - Light meshes:      renderOrder = 10 (renders after glass, always on top)
 *
 * OFF state: emissiveIntensity = 0 → Additive + 0 = invisible (no blending swap)
 *
 * @see docs/ims-engine-api/06-feature-color-system.md
 * @see docs/ims-engine-api/01-architecture.md
 * @see materials/presets/LightGlassPreset.ts
 * @see materials/presets/LightMeshPreset.ts
 */

import * as THREE from 'three';
import type { AssetLoader } from './AssetLoader';
import {
  createHeadlightGlassMaterial,
  createRearlightGlassMaterial,
} from '../materials/presets/LightGlassPreset';
import {
  createWhiteLightMeshMaterial,
  createRedLightMeshMaterial,
} from '../materials/presets/LightMeshPreset';
import type {
  LightSignatureConfig,
  LightSignatureState,
  LightSignatureChangedEvent,
} from '../api/types/lightSignature';

// ---------------------------------------------------------------------------
// Internal slot types
// ---------------------------------------------------------------------------

interface GlassSlot {
  meshes: THREE.Mesh[];
  original: THREE.Material;
  preset: THREE.MeshStandardMaterial;
}

/** Stores per-slot intensity targets so front/rear can be controlled independently. */
interface LightSlot {
  meshes: THREE.Mesh[];
  original: THREE.Material;
  preset: THREE.MeshStandardMaterial;
  /** emissiveIntensity target when fully ON (lerp T = 1) */
  onIntensity: number;
  /** emissiveIntensity target when fully OFF (lerp T = 0) */
  offIntensity: number;
}

// ---------------------------------------------------------------------------
// LightSignatureManager
// ---------------------------------------------------------------------------

export class LightSignatureManager {
  // Per-material-name slot maps
  private _glassSlots = new Map<string, GlassSlot>();
  private _lightSlots = new Map<string, LightSlot>();

  // Fade state
  private _currentIntensity = 0; // 0 = OFF, 1 = ON
  private _targetIntensity = 0;
  private _fadeDurationSec = 0.9; // overridden by config.fadeDurationMs

  // Loaded position-light texture (optional; owned by this manager)
  private _positionTexture: THREE.Texture | null = null;

  // Whether init() has been called
  private _initialized = false;

  // Whether we own an async texture load that might still be in-flight
  private _textureLoaded = false;

  // Whether init() was called with a scene (for getState())
  private _isOn = false;

  // AssetLoader reference stored during init() for runtime texture loading
  private _assetLoader: AssetLoader | null = null;

  // ---------------------------------------------------------------------------
  // Public callbacks (wired by EngineComponents / createEngine)
  // ---------------------------------------------------------------------------

  /**
   * Called when any emissiveIntensity change requires a new render frame.
   * The R3F ticker wires this to `invalidate()` for `frameloop="demand"`.
   */
  onNeedsRender?: () => void;

  /** Called when the light signature is turned on or off. */
  onChanged?: (e: LightSignatureChangedEvent) => void;

  // ---------------------------------------------------------------------------
  // Lifecycle — init
  // ---------------------------------------------------------------------------

  /**
   * Traverse `scene`, find the 4 named material slots, and replace them with
   * internal presets.  Optionally loads the position-light texture.
   *
   * The synchronous traversal + material replacement runs to completion before
   * the first `await`, so the scene is in a consistent state on the next frame
   * even if texture loading is still in-flight.
   *
   * @param scene         The cloned, rendered scene (from onMounted)
   * @param config        Light signature feature config
   * @param assetLoader   Engine's AssetLoader for cache-first texture loading
   */
  async init(
    scene: THREE.Object3D,
    config: LightSignatureConfig,
    assetLoader: AssetLoader,
  ): Promise<void> {
    this.dispose(); // reset any previous state

    this._fadeDurationSec = (config.fadeDurationMs ?? 900) / 1000;

    // --- Synchronous: build preset factories keyed by material name ---

    type GlassEntry = { preset: THREE.MeshStandardMaterial; renderOrder: number }; // factory now returns MeshStandardMaterial
    type LightEntry = {
      preset: THREE.MeshStandardMaterial;
      renderOrder: number;
      onIntensity: number;
      offIntensity: number;
    };

    /** Normalize `string | string[]` to `string[]`. */
    const toArray = (v: string | string[]): string[] => (Array.isArray(v) ? v : [v]);

    const frontGlassPreset = createHeadlightGlassMaterial();
    const rearGlassPreset = createRearlightGlassMaterial();
    const glassDefs = new Map<string, GlassEntry>();
    for (const name of toArray(config.glassFrontMaterialName)) {
      glassDefs.set(name, { preset: frontGlassPreset, renderOrder: 5 });
    }
    for (const name of toArray(config.glassRearMaterialName)) {
      glassDefs.set(name, { preset: rearGlassPreset, renderOrder: 5 });
    }

    const whiteLightPreset = createWhiteLightMeshMaterial();
    const redLightPreset = createRedLightMeshMaterial();
    const lightDefs = new Map<string, LightEntry>();
    for (const name of toArray(config.lightMeshWhiteName)) {
      lightDefs.set(name, {
        preset: whiteLightPreset,
        renderOrder: 10,
        onIntensity: config.frontOnIntensity ?? 3.0,
        offIntensity: config.frontOffIntensity ?? 0.0,
      });
    }
    for (const name of toArray(config.lightMeshRedName)) {
      lightDefs.set(name, {
        preset: redLightPreset,
        renderOrder: 10,
        onIntensity: config.rearOnIntensity ?? 2.0,
        offIntensity: config.rearOffIntensity ?? 0.0,
      });
    }

    // --- Synchronous scene traversal ---

    scene.traverse((obj) => {
      if (!(obj instanceof THREE.Mesh)) return;

      const mat = obj.material;
      if (!mat || Array.isArray(mat)) return;

      const name = (mat as THREE.Material).name;

      // --- Glass slots ---
      const glassDef = glassDefs.get(name);
      if (glassDef) {
        let slot = this._glassSlots.get(name);
        if (!slot) {
          slot = { meshes: [], original: mat, preset: glassDef.preset };
          this._glassSlots.set(name, slot);
        }
        obj.material = glassDef.preset;
        obj.renderOrder = glassDef.renderOrder;
        slot.meshes.push(obj);
        return;
      }

      // --- Light-mesh slots ---
      const lightDef = lightDefs.get(name);
      if (lightDef) {
        let slot = this._lightSlots.get(name);
        if (!slot) {
          slot = {
            meshes: [],
            original: mat,
            preset: lightDef.preset,
            onIntensity: lightDef.onIntensity,
            offIntensity: lightDef.offIntensity,
          };
          this._lightSlots.set(name, slot);
        }
        obj.material = lightDef.preset;
        obj.renderOrder = lightDef.renderOrder;
        obj.frustumCulled = false; // prevent culling flicker at screen edges
        slot.meshes.push(obj);
      }
    });

    this._initialized = true;
    this._textureLoaded = false;
    this._assetLoader = assetLoader;

    // --- Handle position-light texture ---

    if (config.positionLightUrl) {
      // Async: load PNG from URL via AssetLoader (cache-first, retries)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const tex = (await assetLoader.loadTexture(config.positionLightUrl)) as any as THREE.Texture;

      tex.flipY = false;
      tex.wrapS = THREE.RepeatWrapping;
      tex.wrapT = THREE.RepeatWrapping;
      tex.minFilter = THREE.NearestFilter;
      tex.magFilter = THREE.NearestFilter;
      tex.generateMipmaps = false;
      tex.needsUpdate = true;

      this._positionTexture = tex;
      this._applyTextureToLightSlots(tex);
      this._textureLoaded = true;
    } else {
      // Preserve baked-in texture from the original GLB material.
      // Try emissiveMap first (most common for light signatures), then map
      // (diffuse) as a fallback. Only set emissiveMap — do NOT set alphaMap
      // which can conflict with transparency on MeshStandardMaterial.
      for (const slot of this._lightSlots.values()) {
        const original = slot.original as THREE.MeshStandardMaterial;
        const baked = original.emissiveMap ?? original.map ?? null;
        if (baked) {
          slot.preset.emissiveMap = baked;
          slot.preset.needsUpdate = true;
        }
      }
      this._textureLoaded = true; // no external load needed
    }
  }

  // ---------------------------------------------------------------------------
  // Lifecycle — tick (called per-frame by LightSignatureManagerTicker)
  // ---------------------------------------------------------------------------

  /**
   * Advance the emissive intensity lerp and apply to all light-mesh presets.
   * No-op when not fading.
   *
   * @param deltaSec  Time since last frame in seconds (from R3F useFrame)
   */
  tick(deltaSec: number): void {
    if (!this._initialized) return;
    if (this._currentIntensity === this._targetIntensity) return;

    // Cap delta to 50 ms — prevents a large spike after idle (e.g. tab switch
    // or first frame after a long pause) from skipping the entire animation.
    const clampedDelta = Math.min(deltaSec, 0.05);

    const step = this._fadeDurationSec > 0 ? clampedDelta / this._fadeDurationSec : 1; // instant when duration is 0

    if (this._currentIntensity < this._targetIntensity) {
      this._currentIntensity = Math.min(this._currentIntensity + step, this._targetIntensity);
    } else {
      this._currentIntensity = Math.max(this._currentIntensity - step, this._targetIntensity);
    }

    this._applyIntensity(this._currentIntensity);

    // Always request a render after updating intensity — even on the final
    // frame where currentIntensity === targetIntensity — so the completed
    // value is guaranteed to be drawn in demand/invalidate render mode.
    this.onNeedsRender?.();
  }

  // ---------------------------------------------------------------------------
  // Public API — applyPositionTexture (runtime)
  // ---------------------------------------------------------------------------

  /**
   * Load a new position-light PNG at runtime and apply it to the light-mesh
   * presets.  Replaces any previously loaded texture.
   *
   * Accepts any URL — including `blob:` URLs created from a local File object.
   * If `init()` has not been called yet, this is a no-op.
   */
  async applyPositionTexture(url: string): Promise<void> {
    if (!this._initialized || !this._assetLoader) return;

    this._textureLoaded = false;

    // Dispose previously owned texture (blob URLs only need revoking by caller)
    if (this._positionTexture) {
      this._positionTexture.dispose();
      this._positionTexture = null;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tex = (await this._assetLoader.loadTexture(url)) as any as THREE.Texture;
    tex.flipY = false;
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.minFilter = THREE.NearestFilter;
    tex.magFilter = THREE.NearestFilter;
    tex.generateMipmaps = false;
    tex.needsUpdate = true;

    this._positionTexture = tex;
    this._applyTextureToLightSlots(tex);
    this._textureLoaded = true;
    this.onNeedsRender?.();
  }

  // ---------------------------------------------------------------------------
  // Public API — setOn / getState
  // ---------------------------------------------------------------------------

  /**
   * Turn the light signature on or off.
   *
   * @param on        True = ON (full emissive), false = OFF (invisible)
   * @param animated  When true, lerps over fadeDurationMs (default: true)
   */
  setOn(on: boolean, animated = true): void {
    this._isOn = on;
    this._targetIntensity = on ? 1.0 : 0.0;

    if (!animated) {
      this._currentIntensity = this._targetIntensity;
      this._applyIntensity(this._currentIntensity);
    }

    this.onNeedsRender?.();

    this.onChanged?.({ isOn: on, animated });
  }

  /** Current observable state (safe to call any time). */
  getState(): LightSignatureState {
    return {
      isOn: this._isOn,
      intensity: this._currentIntensity,
      textureLoaded: this._textureLoaded,
    };
  }

  /** True while a fade is in progress. Used by the ticker to request frames. */
  get isFading(): boolean {
    return this._initialized && this._currentIntensity !== this._targetIntensity;
  }

  // ---------------------------------------------------------------------------
  // Public API — restoreOriginals (reset)
  // ---------------------------------------------------------------------------

  /**
   * Restore all meshes to their original GLB materials, snap intensity to off,
   * and clear internal state — without disposing the preset materials themselves.
   *
   * Use this to return to the pristine GLB appearance without tearing down
   * the whole manager.  After calling this, `setOn()` will have no visual
   * effect until `init()` is called again.
   *
   * For a full teardown (dispose presets + textures) call `dispose()` instead.
   */
  restoreOriginals(): void {
    for (const slot of this._glassSlots.values()) {
      for (const mesh of slot.meshes) {
        mesh.material = slot.original;
        mesh.renderOrder = 0;
      }
    }
    for (const slot of this._lightSlots.values()) {
      for (const mesh of slot.meshes) {
        mesh.material = slot.original;
        mesh.renderOrder = 0;
        mesh.frustumCulled = true;
      }
    }
    this._currentIntensity = 0;
    this._targetIntensity = 0;
    this._isOn = false;
    this._initialized = false; // prevent setOn() from doing anything visual
    this.onNeedsRender?.(); // make sure the restored state renders
  }

  // ---------------------------------------------------------------------------
  // Lifecycle — dispose
  // ---------------------------------------------------------------------------

  /**
   * Restore original GLB materials, dispose presets, and release texture.
   * Does NOT dispose the original GLB materials (owned by the asset cache).
   */
  dispose(): void {
    // Restore glass meshes
    for (const slot of this._glassSlots.values()) {
      for (const mesh of slot.meshes) {
        mesh.material = slot.original;
        mesh.renderOrder = 0;
      }
      slot.preset.dispose();
    }
    this._glassSlots.clear();

    // Restore light meshes
    for (const slot of this._lightSlots.values()) {
      for (const mesh of slot.meshes) {
        mesh.material = slot.original;
        mesh.renderOrder = 0;
        mesh.frustumCulled = true;
      }
      slot.preset.dispose();
    }
    this._lightSlots.clear();

    // Release owned texture (only if we loaded it — not if we copied from GLB)
    if (this._positionTexture) {
      this._positionTexture.dispose();
      this._positionTexture = null;
    }

    this._initialized = false;
    this._textureLoaded = false;
    this._currentIntensity = 0;
    this._targetIntensity = 0;
    this._isOn = false;
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  private _applyIntensity(t: number): void {
    // t is the lerp parameter (0.0 = OFF state, 1.0 = ON state).
    // Each slot has independent onIntensity / offIntensity so front (white DRL)
    // and rear (red tail) can be tuned separately.
    for (const slot of this._lightSlots.values()) {
      slot.preset.emissiveIntensity =
        slot.offIntensity + (slot.onIntensity - slot.offIntensity) * t;
    }
  }

  private _applyTextureToLightSlots(tex: THREE.Texture): void {
    for (const slot of this._lightSlots.values()) {
      slot.preset.emissiveMap = tex;
      slot.preset.alphaMap = tex;
      slot.preset.needsUpdate = true;
    }
  }
}
